// ABOUTME: Renders candidate details and metadata-driven quick edit forms.
// ABOUTME: Refreshes candidate detail and list data after successful edits.
import { Box, Grid } from "@material-ui/core";
import Loading from "customHooks/useCompleoForm/helpers/Loading";
import React from "react";
import { createStyles, Theme, makeStyles } from "@material-ui/core/styles";
import useCompleoView, {
    UseCompleoEditCustomComponents,
    UseCompleoViewCustomComponents
} from "customHooks/useCompleoView";
import { useApi, useApiCache } from "customHooks/useApi";
import useValuesFromSource from "customHooks/useValuesFromSource";
import useApplicantViewContext from "./useApplicantViewContext";
import ApplicantHistory from "./ApplicantHistory/ApplicantHistory";
import ApplicantEmail from "./ApplicantEmail/ApplicantEmail";
import ApplicantSchedule from "./ApplicantSchedule/ApplicantSchedule";
import ApplicantRating from "./ApplicantRating/ApplicantRating";
import ApplicantQuestionnaires from "./ApplicantQuestionnaires/ApplicantQuestionnaires";
import ApplicantJobMatchAI from "./ApplicantJobMatchAI/index";
import ApplicantOpinionInTheJob from "./ApplicantOpinionInTheJob/ApplicantOpinionInTheJob";
import ApplicantOpinionInTheJobEdit from "./ApplicantOpinionInTheJob/customEditComponent/ApplicantOpinionInTheJobEdit";
import ApplicantCvDescription from "./ApplicantCvDescription/ApplicantCvDescription";
import ApplicantCvDescriptionEdit from "./ApplicantCvDescription/customEditComponent/ApplicantCvDescriptionEdit";
import ApplicantAssessments from "./ApplicantAssessments";
import ApplicantWhatsapp from "./ApplicantWhatsapp";
import { useAuthState } from "_ReactContext/AuthContext";
import { useVerifyJobAccess } from "Pages/Job/util/useVerifyJobAccess";
import { canUseApplicantWhatsappPanel } from "./ApplicantWhatsapp/helpers";
import { mapApplicantEditConflict } from "../ApplicantNewEdit/applicantEditConflict";
import { useWhatsappUnread } from "_ReactContext/WhatsappUnreadContext";
import {
    applyJobContextCustomFieldValues,
    getJobContextCustomFieldNames,
    resolveJobContextCustomFieldsForView,
    isJobContextFieldsReady
} from "./applicantJobContextFields";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            // position: "absolute",
            display: "flex",
            // alignItems: "stretch",
            flexGrow: 1,
            flexDirection: "column",
            backgroundColor: theme.palette.backgroundGrey.main,
            // backgroundColor: "blue",
            height: "100%"
        }
    })
);

interface IProps {
    readOnly: boolean;
}

const ApplicantViewMainView = (props: IProps) => {
    const { readOnly } = props;
    const classes = useStyles();
    const { company } = useAuthState();
    const { getUnreadCountForApplicant } = useWhatsappUnread();
    const verifyJobAccess = useVerifyJobAccess();

    const [data] = useApplicantViewContext();
    const {
        metadata,
        ready,
        queryKey,
        language,
        t,
        timeZone,
        applicantId,
        companyId,
        tabOpen,
        customCVsData,
        evaluationId,
        cvToShare
    } = data;
    const isExternalView = Boolean(data.externalData?.evaluationId);

    // Fetch custom fields used by the metadata-driven applicant view.
    const [customFieldsResponse] = useApiCache(
        "/customfields/customfieldslist",
        "post",
        {
            companyId: companyId,
            module: "APPLICANT"
        },
        isExternalView,
        1000 * 60 * 10
    );
    const authenticatedCustomFields: Compleo.IObject[] =
        customFieldsResponse.response?.data?.fields || [];
    const metadataCustomFields: Compleo.IObject[] =
        data.metadata.response?.data?.metadado || [];
    const customFields = isExternalView
        ? metadataCustomFields
        : authenticatedCustomFields;
    const hasCvDescriptionField = customFields.some(
        (field) => field.customFieldName === "cf_cvDescription"
    );
    const jobContextCustomFields = resolveJobContextCustomFieldsForView(
        authenticatedCustomFields,
        metadataCustomFields,
        isExternalView
    );
    const jobContextCustomFieldNames = getJobContextCustomFieldNames(
        jobContextCustomFields
    );
    const optionalJobContextSelectFieldNames = getJobContextCustomFieldNames(
        jobContextCustomFields.filter(
            (field) =>
                field.fieldType === "list" &&
                field.elementType === "select" &&
                field.required !== true
        )
    );
    const jobContextFieldsReady = isExternalView
        ? isJobContextFieldsReady(data.metadata.status)
        : isJobContextFieldsReady(customFieldsResponse.status);

    const [sourceValues] = useValuesFromSource(
        data.metadata,
        true,
        {},
        data.applicantDataReturn,
        ["data", "data"]
    );
    const valuesFromSource = { ...sourceValues };

    const postAddress = `/applicant/editview`;
    const [editApplicantReturn, editApplicant] = useApi(postAddress, "post");
    const postApplicantEdit = async (values: Compleo.IObject) => {
        const sourceInTheJob = values.sourceInTheJob;
        const hasValidSource =
            sourceInTheJob &&
            typeof sourceInTheJob === "object" &&
            !Array.isArray(sourceInTheJob) &&
            typeof sourceInTheJob.value === "string" &&
            sourceInTheJob.value.length > 0;
        const valuesToSave = { ...values };

        if (!hasValidSource) {
            delete valuesToSave.sourceInTheJob;
        }

        for (const fieldName of optionalJobContextSelectFieldNames) {
            const fieldValue = valuesToSave[fieldName];
            const isEmptyObject =
                fieldValue !== null &&
                typeof fieldValue === "object" &&
                !Array.isArray(fieldValue) &&
                Object.getPrototypeOf(fieldValue) === Object.prototype &&
                Object.keys(fieldValue).length === 0;

            if (
                fieldValue === null ||
                fieldValue === undefined ||
                isEmptyObject
            ) {
                delete valuesToSave[fieldName];
            }
        }

        const response = await editApplicant(valuesToSave);
        return mapApplicantEditConflict(response, t, language);
    };

    const CustomComponents: UseCompleoViewCustomComponents[] = [];
    const CustomEditComponents: UseCompleoEditCustomComponents[] = [];
    const hasJobAccessToWhatsapp = data.jobDataInsideJob
        ? verifyJobAccess(
              data.jobDataInsideJob.jobData ||
                  data.jobDataInsideJob.jobDataContext ||
                  {}
          )
        : true;
    const allowApplicantWhatsapp = canUseApplicantWhatsappPanel({
        whatsappEnabled: company.whatsappEnabled,
        hasJobAccess: hasJobAccessToWhatsapp
    });

    CustomComponents.push(
        {
            fieldName: "applicantHistory",
            Component: ApplicantHistory
        },
        {
            fieldName: "applicantEmail",
            Component: ApplicantEmail
        },
        {
            fieldName: "applicantWhatsapp",
            Component: ApplicantWhatsapp
        },
        {
            fieldName: "applicantSchedule",
            Component: ApplicantSchedule
        },
        {
            fieldName: "applicantRating",
            Component: ApplicantRating
        },
        {
            fieldName: "applicantQuestionnaires",
            Component: ApplicantQuestionnaires
        },
        {
            fieldName: "applicantAssessments",
            Component: ApplicantAssessments
        },
        {
            fieldName: "applicantJobMatch",
            Component: ApplicantJobMatchAI
        }
    );
    const fieldsToHide: string[] = [];
    if (data.jobDataInsideJob) {
        CustomComponents.push({
            fieldName: "opinionOnTheApplicantJob",
            Component: ApplicantOpinionInTheJob
        });
        CustomEditComponents.push({
            name: "opinionOnTheApplicantJob",
            component: ApplicantOpinionInTheJobEdit
        });
        const jobAplicantData = data.applicantData.Jobs.filter(
            (job: Compleo.IObject) => job.JobId === data.jobDataInsideJob?.jobId
        )[0];
        applyJobContextCustomFieldValues(
            valuesFromSource,
            jobAplicantData,
            jobContextCustomFields
        );
        if (jobAplicantData) {
            valuesFromSource.opinionOnTheApplicantJob =
                jobAplicantData.opinionOnTheApplicantJob;
            valuesFromSource.sourceInTheJob = jobAplicantData.source;

            // Add cf_cvDescription field if the company has this custom field
            if (hasCvDescriptionField) {
                CustomComponents.push({
                    fieldName: "cf_cvDescription",
                    Component: ApplicantCvDescription
                });
                CustomEditComponents.push({
                    name: "cf_cvDescription",
                    component: ApplicantCvDescriptionEdit
                });

                // Extract cf_cvDescription from cf_Text array
                const cfText = jobAplicantData.cf_Text || [];
                const cfCvDescription = cfText.find(
                    (cf: { name: string; value: string }) =>
                        cf.name === "cf_cvDescription"
                );
                valuesFromSource.cf_cvDescription = cfCvDescription?.value;
            }
        }
    } else {
        valuesFromSource.sourceInTheJob = undefined;
        fieldsToHide.push("opinionOnTheApplicantJob", "sourceInTheJob");
        if (hasCvDescriptionField) {
            fieldsToHide.push("cf_cvDescription");
        }
        fieldsToHide.push(...jobContextCustomFieldNames);
    }

    if (!allowApplicantWhatsapp) {
        fieldsToHide.push("applicantWhatsapp");
    }

    const addProps: Compleo.useCompleoForm.AdditionalPropertiesField[] = [];

    if (data.externalData?.evaluationId) {
        addProps.push({
            fieldName: "cv",
            props: {
                evaluationId: data.externalData.evaluationId,
                applicantId,
                cvToShare: data?.externalData?.cvToShare
            }
        });
        addProps.push({
            fieldName: "documents",
            props: { evaluationId: data.externalData.evaluationId }
        });
        if (data.externalData?.assessmentsTestsToShare) {
            addProps.push({
                fieldName: "assessments",
                props: {
                    evaluationId: data.externalData.evaluationId,
                    assessmentsTestsToShare:
                        data.externalData.assessmentsTestsToShare
                }
            });
        }
    } else if (evaluationId && cvToShare) {
        addProps.push({
            fieldName: "cv",
            props: {
                evaluationId,
                applicantId,
                cvToShare
            }
        });
    } else {
        addProps.push({
            fieldName: "cv",
            props: {
                applicantId,
                customCVApplicant: {
                    companyId: data.applicantData.companyId,
                    applicantId: data.applicantData.pk?.replace(
                        /^APPLICANT:/,
                        ""
                    ),
                    name: data.applicantData.name
                }
            }
        });
    }

    const { formRetorno, finished } = useCompleoView({
        language: language,
        t: t,
        metadataReturn: metadata,
        ready: ready && jobContextFieldsReady,
        valuesFromSource: valuesFromSource,
        timeZone: timeZone,
        editParams: {
            moduleEditName: "APPLICANTVIEW",
            postFunction: postApplicantEdit,
            postReturn: editApplicantReturn,
            keyData: {
                id: applicantId,
                companyId: companyId,
                jobId: data.jobDataInsideJob?.jobId
            },
            fieldsToHide: fieldsToHide,
            useQueryKeyInvalidate: [queryKey, "/applicant/list"]
        },
        CustomComponents: CustomComponents,
        CustomEditComponents: CustomEditComponents,
        multiStepTabNameToOpen: tabOpen,
        multiStepTabBadges: {
            applicantWhatsapp: getUnreadCountForApplicant(applicantId)
        },
        addProps: addProps,
        readOnly: readOnly,
        fieldsToHide: fieldsToHide,
        customCVsData
    });

    return <Box className={classes.container}>{formRetorno}</Box>;
};

export default ApplicantViewMainView;
